import React, { useEffect, useMemo, useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DoneIcon from "@mui/icons-material/Done";
import { useDarkMode } from "../../context/darkModeContext";
import { useFolderStore } from "../../store/folderStore";
import DeleteRestoreFolder from "./DeleteRestoreFolder";

// images
const FolderImg = `${process.env.PUBLIC_URL}/assets/folder.webp`;

const ThreeDots = `${process.env.PUBLIC_URL}/assets/threedots.webp`;

const Danger = `${process.env.PUBLIC_URL}/assets/danger.webp`;

const RecentDeletedFolders = () => {
  const { darkMode } = useDarkMode();

  const recycleFolders = useFolderStore((state) => state.recycleFolders);

  const loading = useFolderStore((state) => state.recycleLoading);

  const fetchRecycleFolders = useFolderStore(
    (state) => state.fetchRecycleFolders
  );

  const refreshRecycleFolders = useFolderStore(
    (state) => state.refreshRecycleFolders
  );

  const setRefreshRecycleFolders = useFolderStore(
    (state) => state.setRefreshRecycleFolders
  );

  const selectedFolders = useFolderStore((state) => state.selectedFolders);

  const setSelectedFolders = useFolderStore(
    (state) => state.setSelectedFolders
  );

  const toggleFolderSelection = useFolderStore(
    (state) => state.toggleFolderSelection
  );

  const clearSelection = useFolderStore((state) => state.clearSelection);

  const anchorEl = useFolderStore((state) => state.anchorEl);

  const setAnchorEl = useFolderStore((state) => state.setAnchorEl);

  const [displayFolders, setDisplayFolders] = useState([]);

  const [menuFolder, setMenuFolder] = useState(null);

  const [select, setSelect] = useState(false);

  const [openConfirmation, setOpenConfirmation] = useState(false);

  useEffect(() => {
    fetchRecycleFolders();
  }, [fetchRecycleFolders]);

  useEffect(() => {
    if (refreshRecycleFolders) {
      fetchRecycleFolders();
      setRefreshRecycleFolders(false);
    }
  }, [refreshRecycleFolders, fetchRecycleFolders, setRefreshRecycleFolders]);

  useEffect(() => {
    setDisplayFolders(recycleFolders || []);
  }, [recycleFolders]);

  //   const showToast = (severity, message) => {
  //     setSnackbarSeverity(severity);

  //     setSnackbarMessage(message);

  //     setSnackbarOpen(true);
  //   };

  const handleMenuClick = (event, folder) => {
    setAnchorEl(event.currentTarget);

    setMenuFolder(folder);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const isSelected = (folder) =>
    selectedFolders.some((f) => f.id === folder.id);

  const handleFolderClick = (folder) => {
    if (!select) return;

    toggleFolderSelection(folder);
  };

  const toggleMultiSelect = () => {
    if (select) {
      clearSelection();
    }

    setSelect(!select);
  };

  const removeFolder = (folderId) => {
    setDisplayFolders((prev) =>
      prev.filter((folder) => folder.id !== folderId)
    );
  };

  const skeletonCards = useMemo(
    () =>
      Array.from({
        length: 8,
      }),
    []
  );

  if (!loading && displayFolders.length === 0) {
    return (
      <div
        className={`flex justify-center items-center py-20 ${
          darkMode ? "text-gray-400" : "text-gray-500"
        }`}
      >
        No deleted folders found.
      </div>
    );
  }

  return (
    <>
      {/* Toolbar */}

      <div className="flex justify-end items-center gap-4 mb-6">
        {select && (
          <button
            onClick={() => {
              clearSelection();
              setSelect(false);
            }}
            className="px-6 py-2 rounded-lg bg-blue-700 text-white"
          >
            Cancel
          </button>
        )}

        {select && selectedFolders.length > 0 && (
          <button
            onClick={() => setOpenConfirmation(true)}
            className="px-6 py-2 rounded-lg bg-red-600 text-white"
          >
            Delete Selected
          </button>
        )}

        <button
          onClick={() => {
            setSelect(true);

            setSelectedFolders(displayFolders);

            setOpenConfirmation(true);
          }}
          className="px-6 py-2 rounded-lg bg-red-600 text-white"
        >
          Delete All
        </button>

        <button
          onClick={toggleMultiSelect}
          className="px-6 py-2 rounded-lg bg-blue-700 text-white"
        >
          {select ? "Selecting..." : "Select Folders"}
        </button>
      </div>

      {/* Folder Grid */}

      <div
        className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 ${
          darkMode ? "text-white" : "text-gray-800"
        }`}
      >
        {loading
          ? skeletonCards.map((_, index) => (
              <div key={index} className="border rounded-2xl p-4 animate-pulse">
                <div className="h-24 rounded-xl bg-gray-200" />

                <div className="h-5 mt-4 rounded bg-gray-200" />
              </div>
            ))
          : displayFolders.map((folder) => (
              <div
                key={folder.id}
                onClick={() => handleFolderClick(folder)}
                className={`relative border rounded-2xl cursor-pointer transition-all overflow-hidden ${
                  select && isSelected(folder) ? "ring-2 ring-green-500" : ""
                }`}
                style={{
                  background: folder.metadata?.bg || "#ffffff",
                }}
              >
                {/* Selection Overlay */}

                {select && isSelected(folder) && (
                  <div
                    className="absolute inset-0 flex justify-center items-center"
                    style={{
                      background: "rgba(255,255,255,.55)",
                    }}
                  >
                    <div className="bg-green-500 rounded-full p-3">
                      <DoneIcon className="text-white" />
                    </div>
                  </div>
                )}

                {/* Menu */}

                <div className="flex justify-end p-2">
                  <button
                    onClick={(event) => {
                      event.stopPropagation();

                      handleMenuClick(event, folder);
                    }}
                  >
                    <img src={ThreeDots} alt="" width={24} height={24} />
                  </button>
                </div>

                {/* Folder */}

                <div className="flex justify-center">
                  <img src={FolderImg} alt="folder" className="w-24 h-24" />
                </div>

                {/* Name */}

                <div className="px-4 pb-4">
                  <h3 className="font-semibold line-clamp-1 text-center">
                    {folder.name}
                  </h3>
                </div>
              </div>
            ))}
      </div>

      {/* Menu */}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && menuFolder !== null}
        onClose={handleCloseMenu}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          sx={{
            color: "#D1293D",
          }}
        >
          <DeleteRestoreFolder
            id={menuFolder?.id}
            folderName={menuFolder?.name}
            owner={menuFolder?.owner}
            remove={removeFolder}
          />
        </MenuItem>
      </Menu>

      <Dialog
        open={openConfirmation}
        onClose={() => {
          clearSelection();
          setSelect(false);
          setOpenConfirmation(false);
        }}
        PaperProps={{
          style: {
            borderRadius: 8,
            maxWidth: "650px",
          },
        }}
      >
        <DialogContent
          style={{
            background: "#FEF2F2",
          }}
        >
          <div className="flex flex-col items-center gap-5">
            <img src={Danger} alt="" />

            <h2 className="text-2xl font-bold">Delete folders?</h2>

            <div className="text-center">
              <p>The following folders will be moved to the recycle bin.</p>

              <div className="my-6">
                {selectedFolders.map((folder) => (
                  <div key={folder.id} className="font-semibold">
                    {folder.name}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                className="bg-red-600 text-white px-5 py-2 rounded-lg"
                onClick={async () => {
                  try {
                    for (const folder of selectedFolders) {
                      await useFolderStore.getState().recycleFolder(folder.id);
                    }
                  } finally {
                    clearSelection();

                    setSelect(false);

                    setOpenConfirmation(false);

                    setDisplayFolders((prev) =>
                      prev.filter(
                        (folder) =>
                          !selectedFolders.some(
                            (selected) => selected.id === folder.id
                          )
                      )
                    );
                  }
                }}
              >
                Confirm
              </button>

              <button
                className="border px-5 py-2 rounded-lg"
                onClick={() => {
                  clearSelection();

                  setSelect(false);

                  setOpenConfirmation(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RecentDeletedFolders;
